import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { PRIORITY_LABEL, PRIORITY_CLS, ACTION_LABEL, AREA_LABEL, conceptName } from "@/lib/researchLabels";
import { Radar, ChevronDown, ChevronRight, AlertTriangle, Clock, CheckCircle2, PauseCircle, ArrowRight } from "lucide-react";

const AREA_STATE_CLS = {
  NOT_RESEARCHED: "text-zinc-500", IN_PROGRESS: "text-sky-300", COMPLETE: "text-emerald-300",
  BLOCKED: "text-rose-300", NOT_APPLICABLE: "text-zinc-600",
};
const AREA_STATES = ["NOT_RESEARCHED", "IN_PROGRESS", "COMPLETE", "BLOCKED", "NOT_APPLICABLE"];

function ActionDest(action) {
  return { RESOLVE_GROUPING: "/grouping", RESOLVE_ENTITY_MATCH: "/nr-review",
    REVIEW_PRODUCT_IDENTITY: "/nr-review", CHECK_GOOGLE_SEARCH: "/trajectory",
    REVIEW_SUBTYPE: "/grouping", BEGIN_COMMERCIAL_RESEARCH: "/graph",
    REVIEW_DIFFERENTIATION: "/graph", REVIEW_RISK: "/graph", REVIEW_CAPITAL: "/capital-review" }[action] || "/trajectory";
}

export default function ResearchQueue() {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(null);
  const nav = useNavigate();

  const load = () => api.get("/research/queue").then((r) => setData(r.data));
  const [snoozed, setSnoozed] = useState(null);
  const loadSnoozed = () => api.get("/research/snoozed").then((r) => setSnoozed(r.data));
  useEffect(() => { load(); loadSnoozed(); }, []);

  const doWorkflow = async (concept, status, snooze_days) => {
    await api.post("/research/workflow", { concept, status, snooze_days });
    toast.success(status === "SNOOZED" ? "Snoozed (display only — signal unchanged)" : `Marked ${status.toLowerCase()}`);
    load(); loadSnoozed();
  };

  if (!data) return <div className="p-8 text-[#71717A] font-mono text-sm">Loading research queue…</div>;
  const counts = data.counts || {};

  return (
    <div className="p-8 max-w-[1200px]" data-testid="research-queue-page">
      <div className="mb-1 flex items-center gap-2">
        <Radar className="text-sky-400" size={22} />
        <h1 className="font-head text-3xl font-bold tracking-tight">What should I research next?</h1>
      </div>
      <p className="text-[#71717A] text-sm mb-2 max-w-3xl">
        A workflow layer over the live radar — priority means "your attention is valuable now", not "this will make money".
        Missing evidence is never treated as a negative.
      </p>
      <p className="text-[10px] text-amber-300/70 font-mono mb-5">{data.calibration_status}</p>

      {/* priority counts */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mb-8">
        {["P0_ACTION_REQUIRED", "P1_RESEARCH_NOW", "P2_WATCH_CLOSELY", "P3_CONTINUE_MONITORING", "P4_NO_CURRENT_ACTION"].map((p) => (
          <div key={p} className={`border p-3 ${PRIORITY_CLS[p]}`} data-testid={`research-count-${p}`}>
            <div className="font-mono text-2xl font-bold">{counts[p] || 0}</div>
            <div className="text-[10px] uppercase tracking-widest mt-1 opacity-80">{PRIORITY_LABEL[p]}</div>
          </div>
        ))}
      </div>

      <div className="space-y-2">
        {data.items.map((it) => {
          const isOpen = open === it.concept;
          return (
            <div key={it.concept} data-testid={`research-item-${it.concept}`}
              className={`border ${it.workflow.status === "SNOOZED" ? "border-[#1c1c1f] opacity-60" : "border-[#27272A]"} bg-[#101013]`}>
              <button onClick={() => setOpen(isOpen ? null : it.concept)} className="w-full flex flex-wrap items-center gap-3 px-4 py-3 text-left">
                {isOpen ? <ChevronDown size={14} className="text-[#52525B]" /> : <ChevronRight size={14} className="text-[#52525B]" />}
                <span className={`text-[10px] font-mono px-2 py-0.5 border ${PRIORITY_CLS[it.priority]}`}>{PRIORITY_LABEL[it.priority]}</span>
                <span className="font-head font-semibold capitalize">{conceptName(it.concept)}</span>
                {it.blocker && <span className="text-[9px] font-mono px-1.5 py-0.5 border border-rose-400/40 bg-rose-500/10 text-rose-300 flex items-center gap-1"><AlertTriangle size={10} /> BLOCKER</span>}
                <span className="text-xs text-[#71717A]">{it.current_signal_label}</span>
                <span className="text-[10px] font-mono text-[#52525B]">Evidence {it.evidence_grade || "—"}</span>
                {it.workflow.status !== "NEW" && <span className="text-[10px] font-mono text-sky-300">{it.workflow.status}</span>}
                <span className="ml-auto text-xs font-mono text-emerald-300">{ACTION_LABEL[it.next_action] || it.next_action}</span>
              </button>

              {isOpen && (
                <div className="px-4 pb-4 border-t border-[#1c1c1f] pt-3 space-y-3">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                    <div>
                      <div className="text-[10px] uppercase tracking-widest text-emerald-400/70 font-mono mb-1">Why now</div>
                      <ul className="text-[#A1A1AA] text-xs space-y-0.5 list-disc list-inside">{it.why.map((w, i) => <li key={i}>{w}</li>)}</ul>
                    </div>
                    <div>
                      <div className="text-[10px] uppercase tracking-widest text-amber-400/70 font-mono mb-1">What's missing (not a negative)</div>
                      <ul className="text-[#A1A1AA] text-xs space-y-0.5 list-disc list-inside" data-testid={`research-missing-${it.concept}`}>
                        {it.missing_research.map((m, i) => <li key={i}>{m}</li>)}
                      </ul>
                    </div>
                  </div>

                  <div className="flex flex-wrap items-center gap-2">
                    <button onClick={() => nav(ActionDest(it.next_action))} data-testid={`research-do-${it.concept}`}
                      className="text-[11px] font-mono px-3 py-1.5 border border-emerald-400/30 bg-emerald-400/10 text-emerald-300 hover:bg-emerald-400/20 flex items-center gap-1">
                      {ACTION_LABEL[it.next_action] || it.next_action} <ArrowRight size={11} />
                    </button>
                    <button onClick={() => doWorkflow(it.concept, "IN_PROGRESS")} data-testid={`research-progress-${it.concept}`} className="text-[11px] font-mono px-2.5 py-1.5 border border-[#3f3f46] text-[#A1A1AA] hover:bg-[#18181B] flex items-center gap-1"><Clock size={11} /> In progress</button>
                    <button onClick={() => doWorkflow(it.concept, "COMPLETED")} className="text-[11px] font-mono px-2.5 py-1.5 border border-[#3f3f46] text-[#A1A1AA] hover:bg-[#18181B] flex items-center gap-1"><CheckCircle2 size={11} /> Done</button>
                    <button onClick={() => doWorkflow(it.concept, "SNOOZED", 7)} data-testid={`research-snooze-${it.concept}`} className="text-[11px] font-mono px-2.5 py-1.5 border border-[#27272A] text-[#71717A] hover:bg-[#18181B] flex items-center gap-1"><PauseCircle size={11} /> Snooze 7d</button>
                    <span className="text-[10px] font-mono text-[#3f3f46]">Reason codes: {it.reason_codes.join(", ")}</span>
                  </div>

                  <AreaMatrix concept={it.concept} />
                </div>
              )}
            </div>
          );
        })}
      </div>

      {snoozed && snoozed.count > 0 && (
        <div className="mt-8" data-testid="snooze-digest">
          <div className="text-[11px] uppercase tracking-widest text-[#71717A] font-mono mb-2">Snoozed research ({snoozed.count}) — display only, priority preserved</div>
          <div className="space-y-1.5">
            {snoozed.snoozed.map((s) => (
              <div key={s.concept} data-testid={`snooze-item-${s.concept}`} className="border border-[#1c1c1f] bg-[#0c0c0e] px-3 py-2 flex flex-wrap items-center gap-3 text-xs">
                <span className={`text-[10px] font-mono px-2 py-0.5 border ${PRIORITY_CLS[s.priority]}`}>{PRIORITY_LABEL[s.priority]}</span>
                <span className="font-head font-semibold capitalize">{conceptName(s.concept)}</span>
                <span className="text-[#71717A] font-mono">until {(s.snoozed_until || "").slice(0, 10)}</span>
                {s.returns_to_queue && <span className="text-emerald-300 font-mono text-[10px]">due to return</span>}
                {s.last_note && <span className="text-[#52525B] italic">"{s.last_note}"</span>}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function AreaMatrix({ concept }) {
  const [status, setStatus] = useState(null);
  useEffect(() => { api.get("/research/product", { params: { concept } }).then((r) => setStatus(r.data.research_areas)); }, [concept]);
  const cycle = async (area) => {
    const cur = status[area];
    const next = AREA_STATES[(AREA_STATES.indexOf(cur) + 1) % AREA_STATES.length];
    const r = await api.post("/research/area", { concept, area, status: next });
    setStatus(r.data.areas);
  };
  if (!status) return null;
  return (
    <div data-testid={`research-areas-${concept}`}>
      <div className="text-[10px] uppercase tracking-widest text-[#71717A] font-mono mb-1.5">Research areas (workflow state — click to advance)</div>
      <div className="flex flex-wrap gap-1.5">
        {Object.entries(status).map(([area, st]) => (
          <button key={area} onClick={() => cycle(area)} className={`text-[10px] font-mono px-2 py-1 border border-[#27272A] hover:border-[#3f3f46] ${AREA_STATE_CLS[st]}`}>
            {AREA_LABEL[area]}: {st.replace("_", " ").toLowerCase()}
          </button>
        ))}
      </div>
    </div>
  );
}
